/*
  The jump navigations: the tabs under a product's breadcrumb and the
  categories at the top of the FAQ. Both are plain anchor links and work
  without this script. On a phone they had two faults: the highlighted tab
  stayed on the first one whatever you were reading, and once the row had
  scrolled away there was no way to the next section but scrolling back up.

  So, for every [data-hzf-jumpnav]:

  - the link of the section you are reading carries aria-current, and its
    row scrolls it into view;
  - below 990px a compact copy of the row stays at the top of the screen
    (under the header when the header is showing) while you are between
    the first and the last section;
  - a tap lands the section's heading just under that bar.

  Nothing moves: the bar is there or it is not, and the jump is instant, as
  an anchor link is. No new movement for the motion budget.
*/
(() => {
  const NARROW = window.matchMedia('(max-width: 989px)');
  const GAP = 8;
  const bars = [];

  function setUp(nav) {
    const links = [...nav.querySelectorAll('a[href^="#"]')]
      .map((link) => ({ link, target: document.getElementById(decodeURIComponent(link.hash.slice(1))) }))
      .filter((entry) => entry.target);
    if (links.length < 2) return;

    // The compact copy. Built from the links' own labels, so it says what
    // the row says.
    const bar = document.createElement('div');
    bar.className = 'hzf-jumpbar';
    bar.hidden = true;
    const inner = document.createElement('nav');
    inner.className = 'hzf-jumpbar__row';
    inner.setAttribute('aria-label', nav.getAttribute('aria-label') || '');
    const copies = links.map(({ link }) => {
      const copy = document.createElement('a');
      copy.className = 'hzf-jumpbar__link';
      copy.href = link.getAttribute('href');
      copy.textContent = link.dataset.hzfJumpLabel || link.textContent.trim();
      inner.append(copy);
      return copy;
    });
    bar.append(inner);
    document.body.append(bar);

    const section = links[links.length - 1].target.closest('.shopify-section') || document.body;
    const state = { nav, links, copies, bar, inner, section, active: -1 };
    bars.push(state);

    const jump = (event, index) => {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      const target = links[index].target;
      const top = target.getBoundingClientRect().top + window.scrollY;
      // Room for the bar, a gap and a little air, so the heading never sits
      // against the bar's edge.
      const barRoom = NARROW.matches ? (bar.offsetHeight || 56) + GAP : 0;
      let offset = GAP + barRoom + 16;
      const goingUp = top < window.scrollY;
      // Going up, the header slides back in; leave room for it as well.
      if (goingUp) offset += headerHeight();
      window.scrollTo({ top: Math.max(0, top - offset), behavior: 'instant' });
      // Images above the target can still be loading and push it further
      // down after the jump. Going down the header stays away, so the one
      // correction ever needed is a little further down.
      if (!goingUp) {
        setTimeout(() => {
          const drift = target.getBoundingClientRect().top - offset;
          if (drift > 4) window.scrollBy({ top: drift, behavior: 'instant' });
        }, 250);
      }
      history.replaceState(null, '', links[index].link.hash);
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      setActive(state, index);
    };
    links.forEach(({ link }, i) => link.addEventListener('click', (e) => jump(e, i)));
    copies.forEach((copy, i) => copy.addEventListener('click', (e) => jump(e, i)));
  }

  function headerHeight() {
    const header = document.querySelector('.section-header');
    return header ? header.getBoundingClientRect().height : 0;
  }

  function headerBottom() {
    const header = document.querySelector('.section-header');
    if (!header) return 0;
    const box = header.getBoundingClientRect();
    return box.bottom > 0 ? box.bottom : 0;
  }

  function setActive(state, index) {
    if (state.active === index) return;
    state.active = index;
    const mark = (el, on) => (on ? el.setAttribute('aria-current', 'true') : el.removeAttribute('aria-current'));
    state.links.forEach(({ link }, i) => mark(link, i === index));
    state.copies.forEach((copy, i) => mark(copy, i === index));
    // Keep the current link inside its row, without moving the page.
    for (const [row, el] of [
      [state.inner, state.copies[index]],
      [scroller(state.nav), state.links[index].link],
    ]) {
      if (!row || !el) continue;
      const rowBox = row.getBoundingClientRect();
      const box = el.getBoundingClientRect();
      if (box.left < rowBox.left || box.right > rowBox.right) {
        row.scrollLeft += box.left - rowBox.left - (rowBox.width - box.width) / 2;
      }
    }
  }

  function scroller(el) {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
      const overflow = getComputedStyle(node).overflowX;
      if (overflow === 'auto' || overflow === 'scroll') return node;
    }
    return null;
  }

  let queued = false;
  function update() {
    queued = false;
    const top = headerBottom() + GAP;
    for (const state of bars) {
      const navBox = state.nav.getBoundingClientRect();
      const sectionBox = state.section.getBoundingClientRect();
      const show = NARROW.matches && navBox.bottom < top && sectionBox.bottom > top + state.bar.offsetHeight + GAP;
      if (state.bar.hidden === show) state.bar.hidden = !show;
      state.bar.style.setProperty('--hzf-jumpbar-top', `${Math.round(top)}px`);

      // The section being read: the last one whose heading has passed the
      // line under the bar.
      const line = top + (show ? state.bar.offsetHeight : 0) + 48;
      let index = 0;
      state.links.forEach(({ target }, i) => {
        if (target.getBoundingClientRect().top <= line) index = i;
      });
      setActive(state, index);
    }
  }

  function queue() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }

  function start() {
    document.querySelectorAll('[data-hzf-jumpnav]').forEach(setUp);
    if (!bars.length) return;
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    NARROW.addEventListener('change', queue);
    update();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
